import React from 'react';
import { useTranslation } from 'react-i18next';

import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import Upload from '../../../components/form/upload/Upload';
import { SIZE_25MB } from '../../../core/constants/file';
import { FileDetailsModel } from '../../../components/form/upload/file-details/FileDetails';
import { RootState } from '../../../core/state/state';
import ProjectDocumentsService, { UploadConfig } from '../../../core/services/ProjectDocumentsService';
import { getProjectDetails } from '../../../core/state/project/asyncActions';
import { spawnGlobalNotification } from '../../../core/state/global-notifications/asyncActions';
import { GlobalNotificationTypes } from '../../../core/enums/GlobalNotificationTypes';

const LogisticPlanUpload: React.FC = () => {
  const { t } = useTranslation();
  const PDF_ACCEPTED = 'application/pdf';
  const { projectId } = useParams<{ projectId: string }>();
  const projectDetails = useSelector((state: RootState) => state.projects.projectDetails);
  const dispatch = useDispatch();

  const logisticPlans = projectDetails?.projectPlanDocuments ?? [];
  const fileDetails: FileDetailsModel[] = logisticPlans.map((document) => ({
    id: document.id,
    filename: String(document.name),
    location: String(document.fullName),
    uploadedAt: String(document.createdOn),
    uploadedBy: String(document.createdBy)
  }));

  return (
    <Upload
      accept={PDF_ACCEPTED}
      options={{
        maxFiles: 2 - logisticPlans.length,
        maxSize: SIZE_25MB
      }}
      messages={{
        fileTooLarge: t('file.sizeValidation.logisticPlan'),
        fileInvalidType: t('file.typeValidation.logisticPlan'),
        tooManyFiles: t('file.countValidation.logisticPlan')
      }}
      onDrop={async (formData: FormData, config: UploadConfig) => {
        const files = formData.getAll('files') as File[];
        const filenames = files.map((file) => file.name.toLowerCase());
        const duplicateSelected = filenames.some((filename, index) => filenames.indexOf(filename) !== index);
        const duplicateUploaded = logisticPlans.some((document) => filenames.includes(document.name.toLowerCase()));

        if (duplicateSelected || duplicateUploaded) {
          dispatch(
            spawnGlobalNotification({
              type: GlobalNotificationTypes.Error,
              message: t('file.duplicateValidation.default'),
              delay: 5000
            })
          );
          return;
        }

        await ProjectDocumentsService.uploadProjectDocument(Number(projectId), null, formData, config, true);
        dispatch(getProjectDetails(Number(projectId)));
        dispatch(
          spawnGlobalNotification({
            type: GlobalNotificationTypes.Success,
            title: t('common.notification.file.title'),
            message: t('common.notification.file.content')
          })
        );
      }}
      onRemove={async (details) => {
        await ProjectDocumentsService.deleteProjectDocument(Number(projectId), details.id);
        dispatch(getProjectDetails(Number(projectId)));
        dispatch(
          spawnGlobalNotification({
            type: GlobalNotificationTypes.Info,
            title: t('common.notification.file.remove.title'),
            message: t('common.notification.file.remove.content')
          })
        );
      }}
      fileDetails={fileDetails}
      disabled={logisticPlans.length >= 2}
      disabledMessage={t('file.countValidation.logisticPlanLimitReached')}
      removeModal={{
        titleText: t('common.modal.file.remove.title'),
        contentText: t('common.modal.file.remove.content')
      }}
    />
  );
};

export default LogisticPlanUpload;
